import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Icon } from '@/components/common/Icon';
import { LoadingSpinner } from '@/components/common/LoadingSpinner';
import { useAuth } from '@/hooks/useAuth';
import { resolveHomeRoute } from '@/lib/resolveHomeRoute';
import { cn } from '@/lib/utils';
import type { Membership } from '@/api/context/currentContextApi.types';

interface WorkspaceSwitcherProps {
  tone?: 'light' | 'dark';
}

function membershipKey(membership: Membership): string {
  return membership.scopeCode + ':' + (membership.tenantId === null ? '' : membership.tenantId);
}

export function WorkspaceSwitcher({ tone = 'light' }: WorkspaceSwitcherProps) {
  const { t } = useTranslation('navigation');
  const navigate = useNavigate();
  const { memberships, activeMembership, platformPermissions, switchWorkspace } = useAuth();
  const [isOpen, setIsOpen] = useState(false);
  const [isSwitching, setIsSwitching] = useState(false);

  const options: Membership[] = [];
  const seenKeys = new Set<string>();
  for (const membership of memberships) {
    const key = membershipKey(membership);
    if (seenKeys.has(key) === false) {
      seenKeys.add(key);
      options.push(membership);
    }
  }

  const fallbackOption = options[0];
  if (fallbackOption === undefined) {
    return null;
  }

  function labelFor(membership: Membership): string {
    if (membership.scopeCode === 'platform') {
      return t('workspaceSwitcher.platform');
    }
    return membership.tenantName === null ? t('workspaceSwitcher.platform') : membership.tenantName;
  }

  function roleLabelFor(membership: Membership): string {
    if (membership.scopeCode === 'platform') {
      return t('workspaceSwitcher.platformRole');
    }
    if (membership.scopeCode === 'client') {
      return t('workspaceSwitcher.client');
    }
    return membership.isOwner === true
      ? t('workspaceSwitcher.owner')
      : t('workspaceSwitcher.practitioner');
  }

  function isActiveOption(membership: Membership): boolean {
    return (
      activeMembership !== null &&
      activeMembership.scopeCode === membership.scopeCode &&
      activeMembership.tenantId === membership.tenantId
    );
  }

  async function selectWorkspace(membership: Membership) {
    setIsOpen(false);
    if (isActiveOption(membership)) {
      return;
    }
    setIsSwitching(true);
    try {
      await switchWorkspace(membership);
      navigate(resolveHomeRoute(membership.scopeCode, platformPermissions), { replace: true });
    } finally {
      setIsSwitching(false);
    }
  }

  const currentLabel =
    activeMembership === null ? labelFor(fallbackOption) : labelFor(activeMembership);
  const isInteractive = options.length > 1;
  const isDark = tone === 'dark';

  if (isInteractive === false) {
    return (
      <div
        className={cn(
          'font-extrabold tracking-tight truncate',
          isDark ? 'text-zoowhite' : 'text-zooindigo'
        )}
      >
        {currentLabel}
      </div>
    );
  }

  return (
    <div className="relative">
      <button
        type="button"
        disabled={isSwitching}
        aria-haspopup="menu"
        aria-expanded={isOpen}
        aria-busy={isSwitching === true ? true : undefined}
        onClick={function toggleOpen() {
          setIsOpen(isOpen === false);
        }}
        className={cn(
          'w-full flex items-center gap-2 rounded-xl px-2 py-1.5 text-left transition-colors disabled:opacity-60',
          isDark ? 'hover:bg-white/10' : 'hover:bg-zoopalelavender hover:bg-opacity-40'
        )}
      >
        <span
          className={cn(
            'flex-1 min-w-0 font-extrabold tracking-tight truncate',
            isDark ? 'text-zoowhite' : 'text-zooindigo'
          )}
        >
          {currentLabel}
        </span>
        {isSwitching === true ? (
          <LoadingSpinner
            className={cn('w-4 h-4', isDark ? 'border-white/30 border-t-white' : undefined)}
          />
        ) : (
          <Icon name="ChevronDown" className={isDark ? 'text-white/60' : 'text-zoomuted'} />
        )}
      </button>

      {isOpen === true ? (
        <>
          <button
            type="button"
            aria-hidden="true"
            tabIndex={-1}
            onClick={function closeOnBackdrop() {
              setIsOpen(false);
            }}
            className="fixed inset-0 z-40 cursor-default"
          />
          <div
            role="menu"
            className="absolute left-0 right-0 top-full mt-1 z-50 bg-zoowhite border border-zooborder rounded-xl shadow-lg overflow-hidden py-1"
          >
            <div className="px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider text-zoomuted">
              {t('workspaceSwitcher.menuLabel')}
            </div>
            {options.map(function renderOption(membership) {
              const active = isActiveOption(membership);
              return (
                <button
                  key={membershipKey(membership)}
                  type="button"
                  role="menuitem"
                  onClick={function chooseThis() {
                    selectWorkspace(membership);
                  }}
                  className={cn(
                    'w-full flex items-center gap-2 px-3 py-2 text-left transition-colors',
                    active ? 'bg-zoovpalelavender' : 'hover:bg-zoovpalelavender hover:bg-opacity-50'
                  )}
                >
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-semibold text-zooindigo truncate">
                      {labelFor(membership)}
                    </div>
                    <div className="text-[11px] text-zoomuted truncate">
                      {roleLabelFor(membership)}
                    </div>
                  </div>
                  {active ? <Icon name="Check" className="text-zooprimary shrink-0" /> : null}
                </button>
              );
            })}
          </div>
        </>
      ) : null}
    </div>
  );
}
